/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { Stack, StackItem, Text } from "@fluentui/react";

import { COMPETITOR_MESSAGE, GOVERNMENT_EMPLOYEE_MESSAGE } from "Constants/ApplicationConstants";

import { IProfileHeaderProps } from "./IProfileHeaderProps";
import {
  applicationContentStackStyles,
  bolderTextStylesSmall,
  contentStackTokens,
  headerStackStyles,
  headerStackTokens,
  isCompetitorTextStylesSmall,
  normalTextStyles,
  privateProfileTextStyles,
} from "./ProfileHeader.styles";

const ApplicationProfileHeader = (props: Partial<IProfileHeaderProps>): React.ReactElement => {
  const {
    firstName,
    lastName,
    isPrivate,
    technologyAreas,
    isCompetitor,
    isGovtEmployee,
    companyName,
    jobTitle,
    applicationId,
    awardedTechnologyAreas,
    yearsInProgram,
  } = props;

  return (
    <Stack styles={headerStackStyles} tokens={headerStackTokens}>
      <Stack horizontal tokens={contentStackTokens}>
        <StackItem styles={applicationContentStackStyles}>
          <Text
            styles={bolderTextStylesSmall}
          >{`${firstName} ${lastName} [${applicationId}]`}</Text>
        </StackItem>
        {!!technologyAreas && technologyAreas.length > 0 && (
          <StackItem styles={applicationContentStackStyles}>
            <Text styles={bolderTextStylesSmall}>Applying To:</Text>
          </StackItem>
        )}
      </Stack>
      <Stack horizontal tokens={contentStackTokens}>
        <Stack styles={applicationContentStackStyles}>
          <StackItem>
            <Text styles={bolderTextStylesSmall}>
              <Text styles={bolderTextStylesSmall}>Current Awards</Text>
            </Text>
          </StackItem>
          {awardedTechnologyAreas?.map((techAreas, index) => (
            <StackItem>
              <Text>{`Awarded TA ${index + 1}: ${techAreas}`}</Text>
            </StackItem>
          ))}
          {!!isPrivate && (
            <StackItem>
              <Text styles={privateProfileTextStyles}>{"Private Profile"}</Text>
            </StackItem>
          )}
        </Stack>

        {technologyAreas?.map((technologyArea, index) => (
          <Stack styles={applicationContentStackStyles}>
            <StackItem key={technologyArea.technologyFocusAreaName}>
              <StackItem>
                <Text styles={bolderTextStylesSmall}>{technologyArea.technologyFocusAreaName}</Text>
              </StackItem>
              <StackItem>
                <Text>
                  Score:{" "}
                  {technologyArea.technologyFocusAreaScore !== 0
                    ? technologyArea.technologyFocusAreaScore
                    : "Unscored"}
                </Text>
              </StackItem>
              <StackItem>
                <Text>Status: {technologyArea.technologyFocusAreaStatus ?? "N/A"}</Text>
              </StackItem>
            </StackItem>
          </Stack>
        ))}
        <Stack styles={applicationContentStackStyles}>
          {!!isCompetitor ? (
            <StackItem styles={applicationContentStackStyles}>
              <Text styles={isCompetitorTextStylesSmall}>{COMPETITOR_MESSAGE}</Text>
            </StackItem>
          ) : (
            <br />
          )}
          {!!isGovtEmployee ? (
            <StackItem styles={applicationContentStackStyles}>
              <Text styles={isCompetitorTextStylesSmall}>{GOVERNMENT_EMPLOYEE_MESSAGE}</Text>
            </StackItem>
          ) : (
            <br />
          )}
          {!!jobTitle && (
            <StackItem>
              <Text styles={normalTextStyles}>{`Job Title: ${jobTitle}`}</Text>
            </StackItem>
          )}
          {!!companyName && (
            <StackItem>
              <Text
                styles={normalTextStyles}
              >{`Current Employer/Organization: ${companyName}`}</Text>
            </StackItem>
          )}
        </Stack>
      </Stack>
      <Stack horizontal tokens={contentStackTokens}>
        <Stack styles={applicationContentStackStyles}>
          <StackItem>
            <Text styles={bolderTextStylesSmall}>
              <Text styles={bolderTextStylesSmall}>{`Consecutive years: ${yearsInProgram}`}</Text>
            </Text>
          </StackItem>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ApplicationProfileHeader;
